import { Button, Group, Select, Stack, Text, TextInput } from '@mantine/core'
import { isNotEmpty, useForm } from '@mantine/form'
import { IconInfoCircle, IconPlugConnected } from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { Device } from '@/types/device'
import {
  BRIDGE_PATH_PREFIX,
  buildBridgePath,
  isValidBridgeSubPath
} from '@/utils/validation/bridgePath'
import {
  AI_BRIDGE_TEST_RESULT_COLOR,
  AiBridgeTestResult
} from '../useAiBridgeTest'

export type FormValues = {
  name: string
  device_uuid: string
  path: string
}

// Wide enough for the rendered prefix so the editable part starts after it.
const PATH_PREFIX_WIDTH = 160

// The root is fixed in the UI, so the form only holds the part below it. Callers still
// receive the full path.
type PathFormValues = Omit<FormValues, 'path'> & {
  subPath: string
}

type AddAiBridgeFormProps = {
  devices: Device[]
  isTesting?: boolean
  testResult?: AiBridgeTestResult | null
  onTest: (values: { deviceUuid: string; path: string }) => void
  onResetResult?: () => void
  onCancel: () => void
  onSubmit: (formValues: FormValues) => void
}

export const AddAiBridgeForm = ({
  devices,
  isTesting,
  testResult,
  onTest,
  onResetResult,
  onCancel,
  onSubmit
}: AddAiBridgeFormProps) => {
  const intl = useIntl()

  const form = useForm<PathFormValues>({
    initialValues: {
      name: '',
      device_uuid: devices[0]?.id || '',
      subPath: ''
    },
    onValuesChange: onResetResult,
    validate: {
      name: isNotEmpty(
        intl.formatMessage({ id: 'aiBridge.add.form.name.required' })
      ),
      device_uuid: isNotEmpty(
        intl.formatMessage({ id: 'aiBridge.add.form.device.required' })
      ),
      subPath: (value) => {
        if (!isValidBridgeSubPath(value)) {
          return intl.formatMessage(
            { id: 'aiBridge.add.form.path.invalid' },
            { prefix: BRIDGE_PATH_PREFIX }
          )
        }

        return null
      }
    }
  })

  const handleTest = () => {
    if (
      form.validateField('device_uuid').hasError ||
      form.validateField('subPath').hasError
    ) {
      return
    }

    onTest({
      deviceUuid: form.values.device_uuid,
      path: buildBridgePath(form.values.subPath)
    })
  }

  return (
    <form
      noValidate
      onSubmit={form.onSubmit(({ name, device_uuid, subPath }) =>
        onSubmit({
          name,
          device_uuid,
          path: buildBridgePath(subPath)
        })
      )}
    >
      <Stack gap="lg">
        <TextInput
          label={<FormattedMessage id="aiBridge.fields.name" />}
          autoFocus
          data-autofocus // required for auto focus inside the modal
          {...form.getInputProps('name')}
        />

        <Select
          label={<FormattedMessage id="aiBridge.fields.device" />}
          data={devices.map((device) => ({
            value: device.id,
            label: device.label
          }))}
          allowDeselect={false}
          nothingFoundMessage={
            <FormattedMessage id="aiBridge.device.noOnlineDevices" />
          }
          {...form.getInputProps('device_uuid')}
        />

        <Stack gap={6}>
          <TextInput
            label={<FormattedMessage id="aiBridge.fields.path" />}
            placeholder={intl.formatMessage({
              id: 'aiBridge.add.form.path.placeholder'
            })}
            // The backend rejects anything outside the root, so it is shown as a fixed,
            // non-editable part of the field.
            leftSection={
              <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                {BRIDGE_PATH_PREFIX}
              </Text>
            }
            leftSectionWidth={PATH_PREFIX_WIDTH}
            leftSectionPointerEvents="none"
            styles={(theme) => ({
              section: {
                justifyContent: 'flex-start',
                paddingLeft: theme.spacing.xs
              }
            })}
            {...form.getInputProps('subPath')}
          />

          <Group gap={6} wrap="nowrap" align="flex-start">
            <IconInfoCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />

            <Text size="xs" c="dimmed">
              <FormattedMessage
                id="aiBridge.add.form.path.hint"
                values={{ prefix: <span>{BRIDGE_PATH_PREFIX}</span> }}
              />
            </Text>
          </Group>
        </Stack>

        <PlatformAlert
          variant="info"
          title={<FormattedMessage id="aiBridge.add.form.test.hint.title" />}
          compact
        >
          <FormattedMessage id="aiBridge.add.form.test.hint" />
        </PlatformAlert>

        <Stack gap="xs">
          <Group>
            <Button
              type="button"
              size="xs"
              miw={120}
              color="blue.8"
              loading={isTesting}
              leftSection={<IconPlugConnected size={16} />}
              onClick={handleTest}
            >
              <FormattedMessage id="aiBridge.add.form.test" />
            </Button>
          </Group>

          {testResult && (
            <Text size="xs" c={AI_BRIDGE_TEST_RESULT_COLOR[testResult.status]}>
              {testResult.message}
            </Text>
          )}
        </Stack>

        <Group justify="end">
          <Button miw={120} variant="default" onClick={onCancel}>
            <FormattedMessage id="cancel" />
          </Button>

          <Button miw={120} type="submit">
            <FormattedMessage id="aiBridge.add" />
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
